import { Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { TennisBallIcon, getBallLevelColor } from '@/components/ui/tennis-ball-icon';
import { PlayerAvatar } from '@/components/shared/player-avatar';
import { getAttendanceColor } from '@/components/shared/status-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { ListLoadingSkeleton } from '@/components/shared/list-loading-skeleton';
import { getLevelBadgeColor } from '@/components/shared/skill-level-utils';
import type { PlayerSummary } from '@/hooks/use-director-data';

interface PlayerTableProps {
  players: PlayerSummary[];
  isLoading: boolean;
  total: number;
  emptyMessage: string;
  onViewPlayer: (playerId: string) => void;
  onEditPlayer: (player: PlayerSummary) => void;
  onDeletePlayer: (player: PlayerSummary) => void;
}

export function PlayerTable({
  players,
  isLoading,
  total,
  emptyMessage,
  onViewPlayer,
  onEditPlayer,
  onDeletePlayer,
}: PlayerTableProps) {
  if (isLoading) {
    return <ListLoadingSkeleton count={4} itemHeight="h-24" />;
  }

  if (players.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <EmptyState title={emptyMessage} />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {players.map(player => (
          <Card
            key={player.id}
            className="hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => onViewPlayer(player.id)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <PlayerAvatar
                    firstName={player.firstName ?? '?'}
                    lastName={player.lastName ?? '?'}
                    variant="director"
                    size="md"
                  />
                  <div className="min-w-0">
                    <h3 className="font-semibold text-foreground truncate">
                      {player.firstName} {player.lastName}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      {player.skillLevel && (
                        <Badge className={getLevelBadgeColor(player.skillLevel)}>
                          {player.skillLevel}
                        </Badge>
                      )}
                      {(() => {
                        const ball = getBallLevelColor(player.ballLevel);
                        return ball ? (
                          <TennisBallIcon color={ball.color} title={`${ball.label} Ball`} />
                        ) : null;
                      })()}
                      {player.coaches.length > 0 && (
                        <span className="text-sm text-muted-foreground">
                          {(player.coaches[0] as { displayName?: string | null; email: string }).displayName || player.coaches[0].email.split('@')[0]}
                          {player.coaches.length > 1 && ` +${player.coaches.length - 1}`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                  <div className="text-right hidden sm:block">
                    <div className="text-sm text-muted-foreground">Attendance</div>
                    <div className={`font-bold ${getAttendanceColor(player.attendance)}`}>
                      {player.attendance}%
                    </div>
                  </div>
                  {player.lastEvaluation !== null && (
                    <div className="text-right hidden sm:block">
                      <div className="text-sm text-muted-foreground">Last Eval</div>
                      <div className="font-bold text-primary">{player.lastEvaluation.average}/4</div>
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); onEditPlayer(player); }}
                      title="Edit player"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); onDeletePlayer(player); }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete player"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {total > 0 && (
        <p className="text-sm text-muted-foreground text-center">
          Showing {players.length} of {total} players
        </p>
      )}
    </>
  );
}
